import React from 'react';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Box, Button, Flex, Heading, Table, Text } from '@radix-ui/themes';
import { toast } from 'sonner';
import { TableLoaderOverlay } from 'shared/components';
import { DownloadIcon, VisibilityIcon } from 'shared/icons';
import { AppliedJob } from './types';
import { useAppliedJobs } from './applied-jobs.provider';
import './applied-jobs.scss';

const AppliedJobsView = () => {
   const [t] = useTranslation('global');
   const appliedJobsStore = useAppliedJobs();

   React.useEffect(() => {
      appliedJobsStore.init();
      window.scrollTo(0, 0);
   }, [appliedJobsStore]);

   const translateStatus = React.useCallback(
      (status: AppliedJob['status']) => {
         const translations: Record<string, string> = {
            new: t('employerInnerAccountdetail.new'),
            offer: t('employerInnerAccountdetail.offer'),
            accept: t('employerInnerAccountdetail.accept'),
            reviewed: t('employerInnerAccountdetail.reviewed'),
            interview: t('employerInnerAccountdetail.interview'),
            short_list: t('employerInnerAccountdetail.shortlist'),
            not_suitable: t('employerInnerAccountdetail.notSuitable'),
         };

         return translations[status] || status || '';
      },
      [t]
   );

   const renderReviewAction = (job: AppliedJob) => {
      if (job.jobApplyType?.type === 'cv') {
         return (
            <Flex direction="column" align="start" gap="1">
               <Link className="review-action-link" to={`/candidates/my-cv-list/${job.jobApplyType.id}`}>
                  <VisibilityIcon width={18} height={18} />
                  <span>{t('employerInnerAccountdetail.view')}</span>
               </Link>
               <Text className="review-type-text">{t('employerInnerAccountdetail.cv')}</Text>
            </Flex>
         );
      }

      if (job.jobApplyType?.type === 'questionary') {
         return (
            <Flex direction="column" align="start" gap="1">
               <Link
                  className="review-action-link"
                  to={`/candidates/job-applied-view/${job.jobApplyType.id}`}
               >
                  <VisibilityIcon width={18} height={18} />
                  <span>{t('employerInnerAccountdetail.view')}</span>
               </Link>
               <Text className="review-type-text">{t('employerInnerAccountdetail.questionnaire')}</Text>
            </Flex>
         );
      }

      if (job.jobApplyType?.type === 'file') {
         const isLoading = appliedJobsStore.downloadingFileId === job.jobApplyType.id;
         return (
            <Flex direction="column" align="start" gap="1">
               <button
                  className="review-action-link button-reset"
                  type="button"
                  disabled={isLoading}
                  onClick={async () => {
                     const done = await appliedJobsStore.downloadReviewFile(job.jobApplyType.id);
                     if (!done) {
                        toast.error(t('employerFavouriteListProfile.fileDownloadFailedTitle'));
                     }
                  }}
               >
                  <DownloadIcon width={18} height={18} />
                  <span>{t('employerInnerAccountdetail.download')}</span>
               </button>
               <Text className="review-type-text">{t('employerInnerAccountdetail.file')}</Text>
            </Flex>
         );
      }

      return <Text className="review-type-text">-</Text>;
   };

   return (
      <Box className="candidate-applied-jobs">
         <header className="header">
            <Flex align="center" gap="2">
               <img alt="applied-jobs" src="/Images/jobseekerSide/Applied-Jobs-color.png" />
               <Heading as="h3" size="7" className="heading">
                  {t('jobseekerAppliedJobs.appliedJobs')}
               </Heading>
            </Flex>
         </header>

         <Box pb="4">
            <Table.Root className="applied-jobs-table" layout="fixed" variant="ghost">
               <Table.Header className="table-header">
                  <Table.Row className="table-header-row">
                     <Table.ColumnHeaderCell
                        className="table-header-cell"
                        width="60px"
                        justify="center"
                        px="5"
                        py="4"
                     >
                        №
                     </Table.ColumnHeaderCell>

                     <Table.ColumnHeaderCell className="table-header-cell" width="200px" px="5" py="4">
                        {t('jobseekerAppliedJobs.jobTitle')}
                     </Table.ColumnHeaderCell>

                     <Table.ColumnHeaderCell
                        className="table-header-cell"
                        width="200px"
                        px="5"
                        py="4"
                     >
                        {t('jobseekerAppliedJobs.companyName')}
                     </Table.ColumnHeaderCell>

                     <Table.ColumnHeaderCell
                        className="table-header-cell"
                        width="130px"
                        px="5"
                        py="4"
                     >
                        {t('jobseekerAppliedJobs.appliedDate')}
                     </Table.ColumnHeaderCell>

                     <Table.ColumnHeaderCell
                        className="table-header-cell"
                        width="180px"
                        px="5"
                        py="4"
                     >
                        {t('jobseekerAppliedJobs.status')}
                     </Table.ColumnHeaderCell>

                     <Table.ColumnHeaderCell
                        className="table-header-cell"
                        width="180px"
                        px="5"
                        py="4"
                     >
                        {t('jobseekerAppliedJobs.review')}
                     </Table.ColumnHeaderCell>
                  </Table.Row>
               </Table.Header>

               <Table.Body>
                  <TableLoaderOverlay
                     show={appliedJobsStore.loadAppliedJobsState.status === 'pending'}
                     colSpan={6}
                  />

                  {appliedJobsStore.appliedJobs.map((job, idx) => (
                     <Table.Row className="table-row" key={job.id ?? `${job.jobApplyType?.id}-${idx}`}>
                        <Table.Cell className="table-cell" justify="center" px="5" py="4">
                           {appliedJobsStore.coef + idx}
                        </Table.Cell>

                        <Table.Cell className="table-cell" px="5" py="4">
                           {job.title}
                        </Table.Cell>

                        <Table.Cell className="table-cell" px="5" py="4">
                           {job.company_name}
                        </Table.Cell>

                        <Table.Cell className="table-cell" px="5" py="4">
                           {job.created}
                        </Table.Cell>

                        <Table.Cell className="table-cell" px="5" py="4">
                           {translateStatus(job.status)}
                        </Table.Cell>

                        <Table.Cell className="table-cell review-cell" px="5" py="4">
                           {renderReviewAction(job)}
                        </Table.Cell>
                     </Table.Row>
                  ))}
               </Table.Body>
            </Table.Root>
         </Box>

         <Flex className="pagination" align="center" justify="center" pt="4" direction="column" gap="2">
            <Flex>
               <Text color="gray">
                  {t('pagination.NoofRecords')} {appliedJobsStore.recordsCount} of{' '}
                  {appliedJobsStore.rawAppliedJobs.length}
               </Text>
            </Flex>

            <Box>
               <Flex align="center" gap="4">
                  <Button
                     radius="small"
                     size="4"
                     disabled={!appliedJobsStore.canPrev}
                     onClick={() => {
                        appliedJobsStore.onPrev();
                     }}
                  >
                     {t('pagination.Prev')}
                  </Button>

                  <Button
                     radius="small"
                     size="4"
                     disabled={!appliedJobsStore.canNext}
                     onClick={() => {
                        appliedJobsStore.onNext();
                     }}
                  >
                     {t('pagination.Next')}
                  </Button>
               </Flex>
            </Box>
         </Flex>
      </Box>
   );
};

export const AppliedJobs = observer(AppliedJobsView);
